import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api, LOGO_URL } from "@/lib/api";
import { Heart, ShieldCheck, Users, Award, Target, Eye, Sparkles, Quote, Calendar, CheckCircle2, ArrowRight } from "lucide-react";

const FOUNDER_PHOTO_DEFAULT = "/pmr-photos/pmr-7.jpg";

const VALUES = [
  { icon: Heart, title: "Compassion", desc: "We serve with empathy and kindness.", color: "bg-rose-50 text-rose-600" },
  { icon: ShieldCheck, title: "Integrity", desc: "Transparent and accountable in all actions.", color: "bg-emerald-50 text-emerald-600" },
  { icon: Users, title: "Community", desc: "Building together, growing together.", color: "bg-blue-50 text-brand" },
  { icon: Award, title: "Excellence", desc: "Striving for impact in everything we do.", color: "bg-amber-50 text-[#D97706]" },
];

const TIMELINE = [
  { year: "2024", title: "Foundation Established", desc: "PMR Welfare Foundation was registered as a Section 8 company in Patna, Bihar." },
  { year: "2024", title: "First Programs Launched", desc: "Initiated education support and food distribution programs in rural Bihar." },
  { year: "2025", title: "80G Registration", desc: "Received 80G provisional approval enabling tax benefits for donors." },
  { year: "2025", title: "Expanding Reach", desc: "Extending programs across 9 districts of Bihar." },
];

const REG_DETAILS = [
  { k: "Organization Name", v: "PMR WELFARE FOUNDATION" },
  { k: "NGO Darpan ID", v: "BR/2024/0396082" },
  { k: "80G Registration", v: "AAOCP5008QF20251" },
  { k: "Company Type", v: "Section 8 Company" },
  { k: "CIN", v: "U84120BR2024NPL067860" },
  { k: "Registration Date", v: "20th February 2024" },
];

export default function About() {
  const [hp, setHp] = useState(null);
  useEffect(() => { api.get("/public/homepage").then((r) => setHp(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50/60 to-white border-b border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">Our Story</div>
          <h1 className="heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black">About Us</h1>
          <p className="mt-5 text-slate-600 max-w-2xl mx-auto">Building a better tomorrow through compassion, education, and community development.</p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-6">
        <div className="p-8 rounded-3xl border border-slate-200 bg-white hover:shadow-lg transition">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-brand flex items-center justify-center"><Target size={22} /></div>
          <h2 className="heading text-2xl font-black mt-5">Our Mission</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            To empower underprivileged communities in rural Bihar by providing access to quality education, healthcare, nutrition, and livelihood opportunities. We strive to create sustainable change that uplifts entire communities and breaks the cycle of poverty.
          </p>
        </div>
        <div className="p-8 rounded-3xl border border-slate-200 bg-white hover:shadow-lg transition">
          <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#F59E0B] flex items-center justify-center"><Eye size={22} /></div>
          <h2 className="heading text-2xl font-black mt-5">Our Vision</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            A world where every individual, regardless of their background, has equal opportunities to thrive. We envision communities where children can dream big, women are empowered, and families have access to basic necessities for a dignified life.
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">What We Believe</div>
            <h2 className="heading mt-4 text-3xl sm:text-4xl font-black">Our Core Values</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v) => {
              const I = v.icon;
              return (
                <div key={v.title} className="p-6 rounded-2xl bg-white border border-slate-200 hover:-translate-y-1 transition text-center">
                  <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${v.color}`}><I size={22} /></div>
                  <h3 className="heading text-lg font-bold mt-5">{v.title}</h3>
                  <p className="text-sm text-slate-600 mt-2">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-[320px_1fr] gap-10 items-start">
        <div className="lg:sticky lg:top-24">
          <div className="rounded-3xl overflow-hidden border-4 border-brand/10 aspect-[4/5] bg-slate-100">
            <img src={hp?.founder_photo_url || FOUNDER_PHOTO_DEFAULT} alt={hp?.founder_name || "Aashish Kumar Jha"} className="w-full h-full object-cover" />
          </div>
          <div className="mt-4 text-center">
            <div className="heading font-black text-lg">{hp?.founder_name || "Aashish Kumar Jha"}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mt-1">Chairman, PMR Welfare Foundation</div>
          </div>
        </div>
        <div>
          <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">Founder · Leadership</div>
          <h2 className="heading mt-4 text-3xl sm:text-4xl font-black">Founder's Message</h2>
          <Quote className="mt-6 text-brand/20" size={44} />
          <div className="mt-3 space-y-4 text-slate-700 leading-relaxed whitespace-pre-line">
            {hp?.founder_message || `When I started PMR Welfare Foundation, I had a simple dream — to give back to the community that shaped me. Growing up in Bihar, I witnessed the struggles of families who couldn't afford basic education or healthcare for their children.

Today, with the support of our dedicated volunteers and generous donors, we are making a difference in thousands of lives. Every child we educate, every family we feed, and every woman we empower brings us closer to our vision of an equitable society.

I invite you to join us in this journey of transformation. Together, we can build a future where no child goes hungry and no dream goes unfulfilled.`}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <img src={LOGO_URL} alt="PMR" className="w-10 h-10 rounded-full border border-slate-200 bg-white p-0.5 object-contain" />
            <div>
              <div className="font-black">{hp?.founder_name || "Aashish Kumar Jha"}</div>
              <div className="text-xs text-slate-500">{hp?.founder_designation || "Chairman, PMR Welfare Foundation"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Co-Founder section (only shown if admin has filled it in via CMS) */}
      {hp?.founder2_name && (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-[1fr_320px] gap-10 items-start">
            <div>
              <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">Co-Founder · Leadership</div>
              <h2 className="heading mt-4 text-3xl sm:text-4xl font-black">A Note from {hp.founder2_name}</h2>
              <Quote className="mt-6 text-brand/20" size={44} />
              <div className="mt-3 space-y-4 text-slate-700 leading-relaxed whitespace-pre-line">
                {hp?.founder2_message || ""}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <img src={LOGO_URL} alt="PMR" className="w-10 h-10 rounded-full border border-slate-200 bg-white p-0.5 object-contain" />
                <div>
                  <div className="font-black">{hp.founder2_name}</div>
                  <div className="text-xs text-slate-500">{hp?.founder2_designation || "PMR Welfare Foundation"}</div>
                </div>
              </div>
            </div>
            <div className="lg:sticky lg:top-24">
              <div className="rounded-3xl overflow-hidden border-4 border-brand/10 aspect-[4/5] bg-slate-100">
                {hp?.founder2_photo_url ? (
                  <img src={hp.founder2_photo_url} alt={hp.founder2_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">No photo</div>
                )}
              </div>
              <div className="mt-4 text-center">
                <div className="heading font-black text-lg">{hp.founder2_name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mt-1">{hp?.founder2_designation || ""}</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Timeline */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold">Our Journey</div>
            <h2 className="heading mt-4 text-3xl sm:text-4xl font-black">Timeline</h2>
          </div>
          <div className="mt-12 relative">
            <div className="absolute left-[35px] sm:left-1/2 sm:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-brand/20" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <div key={i} className={`relative grid sm:grid-cols-2 gap-6 sm:gap-10 items-center`}>
                  <div className={`pl-20 sm:pl-0 ${i % 2 === 0 ? "sm:text-right" : "sm:col-start-2"}`}>
                    <div className="inline-block heading text-4xl font-black text-[#F59E0B]">{t.year}</div>
                    <h3 className="heading mt-2 text-xl font-bold">{t.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{t.desc}</p>
                  </div>
                  <div className="absolute left-[25px] sm:left-1/2 sm:-translate-x-1/2 top-2 w-5 h-5 rounded-full bg-brand border-4 border-white shadow" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Registration Details */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-brand text-xs font-bold"><Sparkles size={12} className="inline mr-1" /> Official Registration Details</div>
          <h2 className="heading mt-4 text-3xl sm:text-4xl font-black">Verified & Compliant</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">A Section 8 Company registered under the Companies Act, 2013, with NGO Darpan and 80G tax-exemption certification.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {REG_DETAILS.map((r) => (
            <div key={r.k} className="p-5 rounded-xl border border-slate-200 bg-white">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{r.k}</div>
              <div className="heading mt-2 text-lg font-black break-all">{r.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-3 text-sm">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold"><CheckCircle2 size={15} /> 80G Certified</span>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-brand font-bold"><ShieldCheck size={15} /> NGO Darpan Verified</span>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-[#D97706] font-bold"><Award size={15} /> Section 8 Company</span>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#172b66] text-white p-10 sm:p-14 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <h2 className="heading text-3xl sm:text-4xl font-black">Join the mission.</h2>
            <p className="mt-3 text-blue-100">Your support helps us reach more families across Bihar. Start your giving journey today.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/donate" className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold rounded-full px-8 py-3.5 inline-flex items-center gap-2">
              Donate Now <ArrowRight size={16} />
            </Link>
            <Link to="/register" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold rounded-full px-8 py-3.5">
              Volunteer
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter contact={hp} />
      <WhatsAppButton number={hp?.whatsapp_number || "919155561892"} />
    </div>
  );
}
